// The thing we have asked to be shown on this device, if there is one.
//
// Answers null far more often than not, and that is by design: an ask exists
// only where we have a specific gap in what we can drive, and only until
// enough people have answered it. The server decides both — the client never
// sees a fulfilled or retired ask at all.

import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useActiveContextReady } from './useActiveContextReady'
import { config } from '../config'
import { accountScoped } from '@/lib/activeAccount';

export interface CaptureRequest {
  id: string
  /** Phrased to complete "We'd like to learn …" — e.g. "how to create a VLAN". */
  ask: string
}

export function useCaptureRequest(deviceId: string | undefined) {
  const contextReady = useActiveContextReady()
  return useQuery<{ request: CaptureRequest | null }>({
    queryKey: ['deviceCaptureRequest', deviceId],
    queryFn: async () => {
      if (!deviceId) throw new Error('Device ID is required')
      const response = await axios.get<{ request: CaptureRequest | null }>(
        `${config.backendApi}/api/devices/${deviceId}/web-capture-request`,
        { withCredentials: true },
      )
      return response.data
    },
    enabled: !!deviceId && contextReady,
  })
}

/**
 * Asks the customer has waved away, kept per browser.
 *
 * Deliberately not server-side. "Not now" is a mild preference about a mild
 * interruption, and storing it against the account would mean a table, a
 * migration and a cleanup story for something that costs nothing to get
 * slightly wrong. The failure mode is seeing an ask again on another machine,
 * which is survivable — and fulfilment counting already stops an ask reaching
 * most people at all.
 */
const DISMISSED_KEY = 'netman.dismissedCaptureRequests'

function readDismissed(): string[] {
  try {
    const raw = localStorage.getItem(accountScoped(DISMISSED_KEY))
    return raw ? (JSON.parse(raw) as string[]) : []
  } catch {
    // A corrupt or unavailable store must not stop someone reaching their
    // device — treat it as "nothing dismissed".
    return []
  }
}

export function isRequestDismissed(id: string): boolean {
  return readDismissed().includes(id)
}

export function dismissRequest(id: string): void {
  try {
    const all = readDismissed()
    if (!all.includes(id)) {
      localStorage.setItem(accountScoped(DISMISSED_KEY), JSON.stringify([...all, id]))
    }
  } catch {
    /* a dismissal we cannot persist is not worth failing over */
  }
}
